import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from 'react';
const candidateSelectors = [
    'input',
    'select',
    'textarea',
    'a[href]',
    'button:not([disabled])',
    '[tabindex]',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    'details>summary:first-of-type',
    'details',
    'iframe'
];
/**
 * Handles tabbing past last element or before first element from within the TabTrap
 */
const handleTabWrap = ({ e, refFirstFocusable, refLastFocusable, rootElm, refOuter }) => {
    var _a;
    if (e.key !== 'Tab')
        return;
    if (refFirstFocusable.current) {
        if (rootElm.activeElement === refLastFocusable.current && !e.shiftKey) {
            e.preventDefault();
            refFirstFocusable.current.focus();
        }
        if (rootElm.activeElement === refFirstFocusable.current && e.shiftKey) {
            e.preventDefault();
            refLastFocusable.current.focus();
        }
        // refocus if the chilren changed
        if (!((_a = refOuter.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))) {
            refFirstFocusable.current.focus();
        }
    }
};
/**
 * Helper function to extract an element from a string querySelector or ref that a consumer provides.
 */
const getFocusElement = (target) => {
    if (typeof target === 'string')
        return document.querySelector(target);
    if (target === null || target === void 0 ? void 0 : target.current)
        return target.current;
    return null;
};
export const tabbableElementsSelector = candidateSelectors.join(',');
// Disabling because these useEffects are tightly coupled to component scope
// eslint-disable-next-line max-lines-per-function
const TabTrap = ({ children, initialFocusTarget }) => {
    var _a;
    const rootElm = useRef(((_a = document.querySelector('#root > div')) === null || _a === void 0 ? void 0 : _a.shadowRoot) || document);
    const refOuter = useRef(null);
    const refFirstFocusable = useRef(null);
    const refLastFocusable = useRef(null);
    const updateFocusableNodes = useCallback(() => {
        var _a;
        const tabbableNodes = (_a = refOuter.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(tabbableElementsSelector);
        if (tabbableNodes === null || tabbableNodes === void 0 ? void 0 : tabbableNodes.length) {
            const filterNode = [...tabbableNodes].filter((element) => element.offsetWidth > 0 &&
                element.offsetHeight > 0 &&
                element.tabIndex > -1);
            [refFirstFocusable.current] = filterNode;
            refLastFocusable.current = filterNode[filterNode.length - 1];
        }
    }, []);
    const onKeyDown = useCallback((e) => {
        updateFocusableNodes();
        handleTabWrap({
            e,
            refFirstFocusable,
            refLastFocusable,
            rootElm: rootElm.current,
            refOuter
        });
    }, [updateFocusableNodes]);
    useEffect(() => {
        if (refOuter.current) {
            setTimeout(() => {
                var _a;
                updateFocusableNodes();
                const focusElement = getFocusElement(initialFocusTarget);
                if (focusElement) {
                    (_a = focusElement === null || focusElement === void 0 ? void 0 : focusElement.focus) === null || _a === void 0 ? void 0 : _a.call(focusElement);
                }
                else if (refFirstFocusable.current) {
                    // initial focus on modal load
                    refFirstFocusable.current.focus();
                }
            }, 0); // ensures DOM is completely updated
        }
        const rootEl = rootElm.current;
        rootEl.addEventListener('keydown', onKeyDown, {
            capture: true,
            passive: false
        });
        return () => {
            rootEl.removeEventListener('keydown', onKeyDown, true);
        };
    }, [initialFocusTarget, onKeyDown, updateFocusableNodes]);
    return _jsx("div", Object.assign({ ref: refOuter }, { children: children }));
};
export default TabTrap;
//# sourceMappingURL=index.js.map